import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { createContext, useContext, useEffect, useState } from 'react';
import ReactJoyride from 'react-joyride';
import { useHistory } from 'react-router-dom';
import courseService from 'services/course';
import submissionService from 'services/submission';
import { Button, IconButton, Stack, Text, styled } from 'shell/components';
import theme from 'shell/themes';
import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    EXAMPLE_ASSIGNMENT_ID,
    EXAMPLE_SUBMISSION_ID,
} from 'utils/constants';
import { v4 as uuid } from 'uuid';

const JoyrideContext = createContext({});
const useJoyride = () => useContext(JoyrideContext);

const Tooltip = ({ step, closeProps, tooltipProps }) => {
    return (
        <TooltipRootStack {...tooltipProps}>
            <CloseIconButton {...closeProps} kind={'exit'} size={'small'} variant={'square'} />
            {step.content}
        </TooltipRootStack>
    );
};

const joyrideStyles = {
    options: {
        borderRadius: '8px',
        arrowColor: theme.palette.ensightful.primary[500],
        backgroundColor: theme.palette.ensightful.primary[500],
        textColor: theme.palette.ensightful.shades[0],
        zIndex: theme.zIndex.tooltip + 1,
    },
};

const getExampleAssignment = (id, owner_account_id, course_id) => {
    const timestamp = new Date().getTime();
    return {
        id,
        active: true,
        owner_account_id,
        created_on: timestamp,
        modified_on: timestamp,
        course_id,
        title: 'Example Assignment',
        description:
            'Develop a comprehensive marketing plan for a product or service of your choice. This assignment will help you apply marketing principles to real-world scenarios and enhance your strategic planning skills. Start by selecting a product or service, and conduct thorough market research to understand the target audience, market trends, and competitive landscape. Clearly define your marketing objectives and develop a strategy that includes target market identification, positioning, and a detailed marketing mix (4 P’s): product, pricing, distribution, and promotion.',
        rubric: {
            title: 'Assignment Rubric',
            criteria: [
                {
                    id: 'c1',
                    title: 'Market Research',
                    description:
                        'Evaluates the depth and relevance of research into the market, target audience, and competitors.',
                    options: [
                        {
                            id: 'c1-o1',
                            title: 'Excellent',
                            description:
                                'Thorough and insightful research with detailed analysis of market trends, target audience, and competitors; well-supported with data.',
                            rate: '4',
                        },
                        {
                            id: 'c1-o2',
                            title: 'Good',
                            description:
                                'Comprehensive research with relevant analysis; covers market trends and competitors with minor gaps.',
                            rate: '3',
                        },
                        {
                            id: 'c1-o3',
                            title: 'Fair',
                            description:
                                'Basic research with some relevant information; lacks depth in analysis of market trends or competitors.',
                            rate: '2',
                        },
                        {
                            id: 'c1-o4',
                            title: 'Poor',
                            description:
                                'Minimal or incomplete research; lacks relevance or detail in market analysis.',
                            rate: '1',
                        },
                    ],
                },
                {
                    id: 'c2',
                    title: 'Marketing Objectives',
                    description:
                        'Assesses the clarity and feasibility of the marketing objectives outlined in the plan.',
                    options: [
                        {
                            id: 'c2-o1',
                            title: 'Excellent',
                            description:
                                'Objectives are clear, specific, and achievable; well-aligned with overall marketing strategy.',
                            rate: '4',
                        },
                        {
                            id: 'c2-o2',
                            title: 'Good',
                            description:
                                'Objectives are clear and mostly achievable; some minor alignment issues with the marketing strategy.',
                            rate: '3',
                        },
                        {
                            id: 'c2-o3',
                            title: 'Fair',
                            description:
                                'Objectives are somewhat clear but may be vague or partially achievable; alignment with strategy is inconsistent.',
                            rate: '2',
                        },
                        {
                            id: 'c2-o4',
                            title: 'Poor',
                            description:
                                'Objectives are unclear or unrealistic; poor alignment with overall marketing strategy.',
                            rate: '1',
                        },
                    ],
                },
                {
                    id: 'c3',
                    title: 'Marketing Strategy',
                    description:
                        'Evaluates the creativity and effectiveness of the marketing strategy, including target market, positioning, and marketing mix.',
                    options: [
                        {
                            id: 'c3-o1',
                            title: 'Excellent',
                            description:
                                'Highly creative and effective strategy with clear target market, strong positioning, and well-defined marketing mix.',
                            rate: '4',
                        },
                        {
                            id: 'c3-o2',
                            title: 'Good',
                            description:
                                'Effective strategy with a clear target market and positioning; marketing mix is defined with minor improvements needed.',
                            rate: '3',
                        },
                        {
                            id: 'c3-o3',
                            title: 'Fair',
                            description:
                                'Basic strategy with some creativity; target market and positioning are unclear or underdeveloped; marketing mix is partially defined.',
                            rate: '2',
                        },
                        {
                            id: 'c3-o4',
                            title: 'Poor',
                            description:
                                'Weak or ineffective strategy; unclear target market, poor positioning, and incomplete marketing mix.',
                            rate: '1',
                        },
                    ],
                },
                {
                    id: 'c4',
                    title: 'Implementation Plan',
                    description:
                        'Assesses the feasibility and detail of the action plan, including timeline and budget.',
                    options: [
                        {
                            id: 'c4-o1',
                            title: 'Excellent',
                            description:
                                'Detailed and realistic implementation plan with a clear timeline and well-justified budget.',
                            rate: '4',
                        },
                        {
                            id: 'c4-o2',
                            title: 'Good',
                            description:
                                'Feasible implementation plan with a mostly clear timeline and budget; minor details may be missing.',
                            rate: '3',
                        },
                        {
                            id: 'c4-o3',
                            title: 'Fair',
                            description:
                                'Basic implementation plan with some feasibility issues; timeline and budget are present but lack detail or clarity.',
                            rate: '2',
                        },
                        {
                            id: 'c4-o4',
                            title: 'Poor',
                            description:
                                'Incomplete or unrealistic implementation plan; unclear or missing timeline and budget.',
                            rate: '1',
                        },
                    ],
                },
            ],
            sort: 'desc',
        },
        baseline: null,
        ai_feedback_enabled: true,
        ai_feedback_settings: {
            structure: DEFAULT_FEEDBACK_STRUCTURE,
            num_suggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
            suggestion_length: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
            language_level: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
            tone: DEFAULT_FEEDBACK_TONE,
            templated: {},
            custom_instructions: '',
        },
        ai_feedback_context: '',
        ai_grading_enabled: true,
        ai_grading_context: '',
        ai_student_feedback_enabled: false,
    };
};

const getExampleSubmission = (id, assignment_id, owner_account_id, course_id) => {
    const timestamp = new Date().getTime();
    return {
        id,
        active: true,
        owner_account_id,
        created_on: timestamp,
        modified_on: timestamp,
        course_id,
        assignment_id,
        ai: {
            grading: {
                c1: {
                    optionId: 'c1-o1',
                    justification:
                        'The student has conducted thorough and insightful market research. They have provided detailed analysis of market trends, target audience, and competitors, supported by relevant data. This demonstrates a deep understanding of the market dynamics and consumer behavior.',
                    locations: [
                        'Our research highlights a significant trend toward sustainable living, with a growing number of consumers opting for reusable products over single-use plastics. According to recent industry reports, the global reusable water bottle market is projected to grow at a compound annual growth rate (CAGR) of 8% over the next five years.',
                        `Key competitors such as Hydro Flask and S'well dominate the market with their premium, eco-friendly offerings.`,
                    ],
                },
                c2: {
                    optionId: 'c2-o2',
                    justification:
                        'The marketing objectives are clear and mostly achievable but could benefit from further alignment with the overall marketing strategy. While the objectives are specific, there is a lack of detail in how they align with broader strategic goals.',
                    locations: [
                        'Achieve a 20% increase in brand awareness within six months through targeted digital marketing and influencer campaigns.',
                        'Attain a 12% market share in the eco-friendly water bottle segment within the first year by expanding distribution channels and enhancing brand visibility.',
                    ],
                },
                c3: {
                    optionId: 'c3-o2',
                    justification:
                        'The marketing strategy is effective with a clear target market and positioning. However, the marketing mix could be further developed to address minor gaps, such as more detailed tactics for highlighting product features in marketing materials, clearer justification for the pricing strategy, enhanced distribution plans, and specific promotional content strategies.',
                    locations: [
                        'The EcoWave Eco-Friendly Water Bottle is positioned as a premium, sustainable alternative to single-use plastic bottles.',
                        'Customizable options such as personalized engravings will be offered to enhance customer appeal.',
                    ],
                },
                c4: {
                    optionId: 'c4-o3',
                    justification:
                        'The implementation plan is basic and lacks detailed feasibility. While it includes a timeline and budget, there are significant gaps in the details provided, making it less clear how the plan will be executed effectively.',
                    locations: [
                        'Month 1-2: Finalize product design and secure manufacturing. Develop and launch the e-commerce website with integrated payment processing and inventory management systems.',
                        'The estimated budget for the marketing plan is $50,000, allocated as follows: Production: $25,000 (includes costs for manufacturing, packaging, and quality control).',
                    ],
                },
            },
            feedback: {
                feedback_type: 'system',
                commentary:
                    'Your marketing plan for EcoWave is comprehensive and well-structured. You have demonstrated a deep understanding of market research, clearly defined marketing objectives, and a highly creative and effective marketing strategy. The implementation plan is detailed and realistic, showcasing your ability to plan and execute a marketing strategy effectively.',
                improvement_suggestions: [
                    {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        text: '<p style="font-weight: 600;">Clarify Measurement Metrics</p><ul><li><p>Specify the exact metrics or KPIs (Key Performance Indicators) you will use to measure outcomes like increasing brand awareness by 20% and capturing 12% market share.</p></li><li><p>Break down objectives into more detailed milestones (e.g., quarterly goals) to enhance tracking and accountability.</p></li></ul>',
                        locations: [
                            'Achieve a 20% increase in brand awareness within six months through targeted digital marketing and influencer campaigns.',
                            'Attain a 12% market share in the eco-friendly water bottle segment within the first year by expanding distribution channels and enhancing brand visibility.',
                        ],
                    },
                    {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        text: '<p style="font-weight: 600;">Expand on Marketing Mix Details</p><ul><li><p>Provide more specific details on the promotional tactics, such as examples of potential social media campaigns or influencer partnerships.</p></li><li><p>Elaborate on the distribution strategy, including potential retail partners or online platforms.</p></li></ul>',
                        locations: [],
                    },
                    {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        text: '<p style="font-weight: 600;">Refine Implementation Plan</p><ul><li><p>Add more milestones to the timeline to break down the six-month period into more specific tasks.</p></li><li><p>Provide a more detailed breakdown of the budget to show how each cost was estimated.</p></li></ul>',
                        locations: [],
                    },
                ],
            },
        },
        data: {
            name: 'Marketing Plan for EcoWave.pdf',
            type: 'application/pdf',
            size: 67967,
            bucket: 'timelygrader-us-upload-app-sandbox',
            key: '99812815-e08e-4f8f-87ab-2dca4010329c',
        },
    };
};

const JoyrideProvider = ({ children }) => {
    const appContext = useApp();
    const { auth, updateCurrentUser } = useAuth();
    const history = useHistory();
    const { device } = useViewport();

    const [shouldRender, renderTrigger] = useState(true);
    const [joyrideRun, setJoyrideRun] = useState(false);
    const [joyridePaused, setJoyridePaused] = useState(false);
    const [joyrideStepIndex, setJoyrideStepIndex] = useState(0);
    const [nextCallback, setNextCallback] = useState({});
    const [prevCallback, setPrevCallback] = useState({});

    const performNext = async () => {
        if (joyrideStepIndex === 2) {
            const exampleAssignment = getExampleAssignment(EXAMPLE_ASSIGNMENT_ID, auth.id, appContext.course.id);
            appContext.setAssignments((current) => [...current, exampleAssignment]);
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 3) {
            history.push(`/assignments/${EXAMPLE_ASSIGNMENT_ID}`);
            setTimeout(() => {
                setJoyrideStepIndex((current) => current + 1);
            }, 10);
        }

        if (joyrideStepIndex === 4) {
            const exampleSubmission = getExampleSubmission(
                EXAMPLE_SUBMISSION_ID,
                EXAMPLE_ASSIGNMENT_ID,
                auth.id,
                appContext.course.id
            );
            appContext.setSubmissions((current) => [...(current || []), exampleSubmission]);
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 5) {
            history.push(`/assignments/${EXAMPLE_ASSIGNMENT_ID}/grading/${EXAMPLE_SUBMISSION_ID}`);
            setTimeout(() => {
                setJoyrideStepIndex((current) => current + 1);
            }, 10);
        }

        if (joyrideStepIndex === 6) {
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 7) {
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 8) {
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 9) {
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 10) {
            const newAssignmentId = uuid();
            appContext.setAssignments((current) => {
                return current.map((assignment) => {
                    if (assignment.id !== EXAMPLE_ASSIGNMENT_ID) return assignment;
                    courseService.createCourseAssignment(appContext.course.id, { ...assignment, id: newAssignmentId });
                    return { ...assignment, id: newAssignmentId };
                });
            });
            appContext.setSubmissions((current) => {
                return current.map((submission) => {
                    if (submission.id !== EXAMPLE_SUBMISSION_ID) return submission;
                    submissionService.createSubmission({ ...submission, id: uuid(), assignment_id: newAssignmentId });
                    return { ...submission, id: uuid(), assignment_id: newAssignmentId };
                });
            });
            history.push('/assignments');
            updateCurrentUser({ finished_onboarding: true });
            setJoyrideStepIndex((current) => current + 1);
        }

        if (joyrideStepIndex === 11) {
            setJoyrideRun(false);
            history.push('/assignments/new');
        }

        if (nextCallback[joyrideStepIndex]) nextCallback[joyrideStepIndex]();
    };

    const performPrev = () => {
        if (joyrideStepIndex === 3) {
            setJoyrideStepIndex((current) => current - 1);
            appContext.setAssignments([]);
        }

        if (joyrideStepIndex === 4) {
            history.goBack();
            setTimeout(() => {
                setJoyrideStepIndex((current) => current - 1);
            }, 10);
        }

        if (joyrideStepIndex === 5) {
            appContext.setSubmissions([]);
            setJoyrideStepIndex((current) => current - 1);
        }

        if (joyrideStepIndex === 6) {
            history.goBack();
            setTimeout(() => {
                setJoyrideStepIndex((current) => current - 1);
            }, 10);
        }

        if (joyrideStepIndex === 7) {
            setJoyrideStepIndex((current) => current - 1);
        }

        if (joyrideStepIndex === 8) {
            setJoyrideStepIndex((current) => current - 1);
        }

        if (joyrideStepIndex === 9) {
            setJoyrideStepIndex((current) => current - 1);
        }

        if (joyrideStepIndex === 10) {
            setJoyrideStepIndex((current) => current - 1);
        }

        if (prevCallback[joyrideStepIndex]) prevCallback[joyrideStepIndex]();
    };

    const joyrideCallback = (data) => {
        const { action, index } = data;
        if (action === 'close') {
            if (index === 0 || index === 1) {
                setJoyridePaused(true);
            } else if (index === 2) {
                setJoyridePaused(true);
                updateCurrentUser({ finished_onboarding: true });
                setTimeout(() => {
                    setJoyrideStepIndex(11);
                    setJoyridePaused(false);
                }, 500);
            } else if (index === 11) {
                setJoyrideRun(false);
            } else {
                setJoyridePaused(true);
                updateCurrentUser({ finished_onboarding: true });
                const assignmentId = uuid();
                const assignment = getExampleAssignment(assignmentId, auth.id, appContext.course.id);
                const submission = getExampleSubmission(uuid(), assignmentId, auth.id, appContext.course.id);
                appContext.setAssignments((current) => [
                    ...current.filter(({ id }) => id !== EXAMPLE_ASSIGNMENT_ID),
                    assignment,
                ]);
                courseService.createCourseAssignment(appContext.course.id, assignment);
                submissionService.createSubmission(submission);
                history.push('/assignments');
                setTimeout(() => {
                    setJoyrideStepIndex(11);
                    setJoyridePaused(false);
                }, 500);
            }
        }
    };

    const joyrideSteps = [
        {
            target: '.joyride-step-1',
            content: (
                <Stack alignItems={'flex-start'} spacing={2}>
                    <Text color={'inherit'} component={'p'} variant={'h6'}>
                        Create Your First Course
                    </Text>
                    <Text color={'inherit'}>
                        Welcome to TimelyGrader! Get started by creating your first course. Click the “New Course”
                        button to begin setting up your course details.
                    </Text>
                </Stack>
            ),
            disableBeacon: true,
            disableOverlay: true,
            offset: -5,
        },
        {
            target: '.joyride-step-2',
            content: (
                <Stack alignItems={'flex-start'} spacing={2}>
                    <Text color={'inherit'} component={'p'} variant={'h6'}>
                        Access the Assignments Page
                    </Text>
                    <Text color={'inherit'}>
                        Next, click on your new course to explore how TimelyGrader's grading and feedback features work
                        with an example assignment.
                    </Text>
                </Stack>
            ),
            disableBeacon: true,
            disableOverlay: true,
        },
        {
            target: '.joyride-step-3',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            1 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Creating Assignments
                        </Text>
                        <Stack spacing={4}>
                            <Text color={'inherit'}>
                                After creating a new course, the next step is to add an assignment. The assignment
                                details you provide will be the foundation for all of TimelyGrader’s grading and
                                feedback suggestions.
                            </Text>
                            <Text component={'i'} color={'inherit'}>
                                Note: If your course is LMS integrated, you can import assignment details from your LMS.
                            </Text>
                        </Stack>
                    </Stack>
                    <NextButton sx={{ alignSelf: 'flex-end' }} onClick={performNext}>
                        Next
                    </NextButton>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            spotlightClicks: true,
        },
        {
            target: '.joyride-step-4',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            2 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            How Does It Work?
                        </Text>
                        <Text color={'inherit'}>
                            Here’s an example assignment so you can get a sense of what the grading and feedback process
                            looks like. Click on this assignment to continue.
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Next</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            spotlightClicks: true,
        },
        {
            target: '.joyride-step-5',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            3 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Uploading Submissions
                        </Text>
                        <Stack spacing={4}>
                            <Text color={'inherit'}>
                                After setting up your assignment, you can upload student submissions here to receive
                                grading and feedback suggestions from TimelyGrader.
                            </Text>
                            <Text component={'i'} color={'inherit'}>
                                Note: If your course is LMS integrated, you can sync student submissions from your LMS.
                            </Text>
                        </Stack>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton disabled={!appContext.submissions} onClick={performNext}>
                            Next
                        </NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            spotlightClicks: true,
        },
        {
            target: '.joyride-step-6',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            4 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Reviewing Grades and Feedback
                        </Text>
                        <Text color={'inherit'}>
                            Once submissions have finished processing, click here to review the grading and feedback
                            suggestions.
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Next</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            spotlightClicks: true,
        },
        {
            target: '.joyride-step-7',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            5 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Student Submission View
                        </Text>
                        <Text color={'inherit'}>
                            This section is where you will see students’ submissions. TimelyGrader highlights key
                            sections related to the selected grading or feedback suggestion to make your review process
                            more efficient.
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Next</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            spotlightClicks: true,
            spotlightPadding: 0,
        },
        {
            target: '.joyride-step-8',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            6 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Selecting Grades
                        </Text>
                        <Text color={'inherit'}>
                            If you’ve enabled grading for this assignment, this is where you will see grading
                            suggestions for each criterion. TimelyGrader also provides a justification for each grading
                            suggestion to assist you in making the final grading decision.
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Next</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            placement: 'left',
            spotlightClicks: true,
            spotlightPadding: 0,
        },
        {
            target: '.joyride-step-9',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            7 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Switching to Feedback View
                        </Text>
                        <Text color={'inherit'}>
                            After grading each criterion, click this toggle to switch to the feedback view, where you
                            can review and edit the suggested feedback.
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Next</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            placement: 'left',
            spotlightClicks: true,
        },
        {
            target: '.joyride-step-10',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            8 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Reviewing and Editing Feedback
                        </Text>
                        <Text color={'inherit'}>
                            Review and edit feedback as needed here. We have several different feedback structures to
                            choose from when setting up your assignment, so feedback might look slightly different
                            depending on what you select.
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Next</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            placement: 'left',
            spotlightClicks: true,
            spotlightPadding: 0,
        },
        {
            target: '.joyride-step-11',
            content: (
                <Stack alignItems={'flex-start'} spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'inherit'} variant={'p3'}>
                            9 of 9
                        </Text>
                        <Text color={'inherit'} component={'p'} variant={'h6'}>
                            Confirming Grades and Feedback
                        </Text>
                        <Text color={'inherit'}>
                            When you’ve selected grades, and reviewed feedback you have to click confirm to save them.
                            Repeat this process until you’ve finished grading all submissions!
                        </Text>
                    </Stack>
                    <Stack sx={{ width: '100%' }} direction={'row-reverse'} spacing={2}>
                        <NextButton onClick={performNext}>Finish</NextButton>
                        <PrevButton variant={'tertiary'} onClick={performPrev}>
                            Back
                        </PrevButton>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableCloseOnEsc: true,
            disableOverlayClose: true,
            disableScrolling: true,
            placement: 'left',
            spotlightClicks: true,
        },
        {
            target: '.joyride-step-12',
            content: (
                <Stack alignItems={'flex-start'} spacing={2}>
                    <Text color={'inherit'} component={'p'} variant={'h6'}>
                        Create Your First Assignment
                    </Text>
                    <Stack spacing={4}>
                        <Text color={'inherit'}>
                            Now that you have a sense of how TimelyGrader works, click here to create your first
                            assignment.
                        </Text>
                        <Text component={'i'} color={'inherit'}>
                            Tip: Even if you don’t have a current assignment, try using a past assignment to get more
                            familiar with the platform!
                        </Text>
                    </Stack>
                </Stack>
            ),
            disableBeacon: true,
            disableOverlay: true,
            offset: -5,
        },
    ];

    useEffect(() => {
        renderTrigger(false);
        setTimeout(() => {
            renderTrigger(true);
        }, 50);
    }, [device]);

    const value = {
        joyrideRun,
        setJoyrideRun,
        setJoyridePaused,
        joyrideStepIndex,
        setJoyrideStepIndex,
        setNextCallback,
        setPrevCallback,
        performNext,
        performPrev,
    };

    const isJoyrideRunning = joyrideRun && !joyridePaused;

    return (
        <JoyrideContext.Provider value={value}>
            {shouldRender && (
                <ReactJoyride
                    callback={joyrideCallback}
                    disableScrollParentFix
                    run={isJoyrideRunning}
                    stepIndex={joyrideStepIndex}
                    steps={joyrideSteps}
                    styles={joyrideStyles}
                    tooltipComponent={Tooltip}
                />
            )}

            {children}
        </JoyrideContext.Provider>
    );
};

const TooltipRootStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        borderRadius: '8px',
        maxWidth: '393px',
        padding: theme.spacing(4, 5),
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

const CloseIconButton = styled(IconButton)(({ theme }) => {
    return {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(4),
        color: theme.palette.ensightful.shades[0],
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.ensightful.secondary[100],
        },
    };
});

const NextButton = styled(Button)(({ theme }) => {
    return {
        width: '62px',
        background: theme.palette.ensightful.shades[0],
        color: theme.palette.ensightful.primary[500],
        '&:hover': {
            background: theme.palette.ensightful.neutral[100],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[200],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.ensightful.secondary[100],
        },
        '&:disabled': {
            background: theme.palette.ensightful.primary[500],
            color: theme.palette.ensightful.neutral[400],
        },
    };
});

const PrevButton = styled(Button)(({ theme }) => {
    return {
        width: '62px',
        color: theme.palette.ensightful.shades[0],
        '&:hover': {
            borderColor: theme.palette.ensightful.neutral[100],
            color: theme.palette.ensightful.shades[0],
        },
        '&:active': {
            borderColor: theme.palette.ensightful.neutral[200],
            color: theme.palette.ensightful.shades[0],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.ensightful.secondary[100],
        },
    };
});

export { useJoyride };
export default JoyrideProvider;
